import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Camera, Printer, AlertCircle, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  PartDetails,
  PartBin,
  PartTimePhased,
  PurchaseHist,
  ApprovedSupplier,
  TransHistory,
  QuoteHistory,
  CustomerRef,
  OrderHistory,
  ProdHistory,
  CustomerPriceList,
  MethodNode,
  WhereUsed,
  PictureItem,
} from '../services/types';
import { apiService } from '../services';
import { Button } from '../components/ui';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorBanner } from '../components/tools/ErrorBanner';
import { PartInformationSection } from '../components/parts/PartInformationSection';
import { PartBinsTable } from '../components/parts/PartBinsTable';
import { PartTimePhasedTable } from '../components/parts/PartTimePhasedTable';
import { PartPurchaseHistTable } from '../components/parts/PartPurchaseHistTable';
import { PartApprovedSuppliersTable } from '../components/parts/PartApprovedSuppliersTable';
import { PartTransHistTable } from '../components/parts/PartTransHistTable';
import { PartQuoteHistoryTable } from '../components/parts/PartQuoteHistoryTable';
import { PartCustomerRefsTable } from '../components/parts/PartCustomerRefsTable';
import { PartOrderHistoryTable } from '../components/parts/PartOrderHistoryTable';
import { PartProdHistTable } from '../components/parts/PartProdHistTable';
import { PartCustomerPriceTable } from '../components/parts/PartCustomerPriceTable';
import MethodTree, { MethodTreeState } from '../components/parts/MethodTree';
import { PartWhereUsedTable } from '../components/parts/PartWhereUsedTable';
import { PartForecastCard } from '../components/parts/PartForecastCard';
import { Breadcrumb } from '../components/Breadcrumb';
import { getCachedData, setCachedData } from '../utils/pageCache';

interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface TransHistSectionState {
  data: TransHistory[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  morePages: boolean;
  currentPage: number;
}

export const PartDetailsPage: React.FC = () => {
  const { partNum } = useParams<{ partNum: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Main part details
  const [partDetails, setPartDetails] = useState<PartDetails | null>(null);
  const [mainLoading, setMainLoading] = useState(true);
  const [mainError, setMainError] = useState<string | null>(null);

  // Optional sections - each fetches independently
  const [binsSection, setBinsSection] = useState<SectionState<PartBin[]>>({
    data: null,
    loading: true,
    error: null,
  });

  const [timePhasedSection, setTimePhasedSection] = useState<
    SectionState<PartTimePhased[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [purchaseHistSection, setPurchaseHistSection] = useState<
    SectionState<PurchaseHist[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [approvedSuppliersSection, setApprovedSuppliersSection] = useState<
    SectionState<ApprovedSupplier[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [transHistSection, setTransHistSection] =
    useState<TransHistSectionState>({
      data: [],
      loading: true,
      loadingMore: false,
      error: null,
      morePages: false,
      currentPage: 1,
    });

  const [quoteHistSection, setQuoteHistSection] = useState<
    SectionState<QuoteHistory[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [customerRefsSection, setCustomerRefsSection] = useState<
    SectionState<CustomerRef[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [orderHistSection, setOrderHistSection] = useState<
    SectionState<OrderHistory[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [prodHistSection, setProdHistSection] = useState<
    SectionState<ProdHistory[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [customerPriceSection, setCustomerPriceSection] = useState<
    SectionState<CustomerPriceList>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [methodSection, setMethodSection] = useState<SectionState<MethodNode>>({
    data: null,
    loading: true,
    error: null,
  });

  const [whereUsedSection, setWhereUsedSection] = useState<
    SectionState<WhereUsed[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  const [picturesSection, setPicturesSection] = useState<
    SectionState<PictureItem[]>
  >({
    data: null,
    loading: true,
    error: null,
  });

  // Method tree UI state (expanded nodes, loaded children)
  const [methodTreeState, setMethodTreeState] = useState<
    MethodTreeState | undefined
  >(undefined);

  // Quick search state
  const [quickSearchInput, setQuickSearchInput] = useState('');
  const [quickSearchError, setQuickSearchError] = useState<string | null>(null);
  const [quickSearchLoading, setQuickSearchLoading] = useState(false);

  // Fetch main part details
  useEffect(() => {
    const fetchPartDetails = async () => {
      if (!partNum) {
        setMainError('Part number is required');
        setMainLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `partDetails_${partNum}`;
      const cached = getCachedData<PartDetails>(cacheKey);
      if (cached) {
        setPartDetails(cached);
        setMainLoading(false);
        return;
      }

      try {
        setMainLoading(true);
        setMainError(null);
        const details = await apiService.getPartDetails(partNum);
        setPartDetails(details);
        setCachedData(cacheKey, details);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load part details';
        setMainError(errorMessage);
      } finally {
        setMainLoading(false);
      }
    };

    fetchPartDetails();
  }, [partNum]);

  // Fetch part pictures (independent — soft fail, pictures are optional)
  useEffect(() => {
    if (!partNum) return;
    setPicturesSection({ data: null, loading: true, error: null });
    apiService
      .getPartPictures(partNum)
      .then((pics) =>
        setPicturesSection({ data: pics, loading: false, error: null })
      )
      .catch(() =>
        setPicturesSection({ data: [], loading: false, error: null })
      );
  }, [partNum]);

  // Fetch part bins (independent)
  useEffect(() => {
    const fetchBins = async () => {
      if (!partNum) return;

      const cacheKey = `partBins_${partNum}`;
      const cached = getCachedData<PartBin[]>(cacheKey);
      if (cached) {
        setBinsSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setBinsSection({ data: null, loading: true, error: null });
        const bins = await apiService.getPartBins(partNum);
        setBinsSection({ data: bins, loading: false, error: null });
        setCachedData(cacheKey, bins);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load part bins';
        setBinsSection({ data: null, loading: false, error: errorMessage });
      }
    };

    fetchBins();
  }, [partNum]);

  // Fetch time phased data (independent)
  useEffect(() => {
    const fetchTimePhased = async () => {
      if (!partNum) return;

      const cacheKey = `partTimePhased_${partNum}`;
      const cached = getCachedData<PartTimePhased[]>(cacheKey);
      if (cached) {
        setTimePhasedSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setTimePhasedSection({ data: null, loading: true, error: null });
        const timePhased = await apiService.getPartTimePhased(partNum);
        setTimePhasedSection({ data: timePhased, loading: false, error: null });
        setCachedData(cacheKey, timePhased);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load time phased data';
        setTimePhasedSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchTimePhased();
  }, [partNum]);

  // Fetch purchase history (independent)
  useEffect(() => {
    const fetchPurchaseHist = async () => {
      if (!partNum) return;

      const cacheKey = `partPurchaseHist_${partNum}`;
      const cached = getCachedData<PurchaseHist[]>(cacheKey);
      if (cached) {
        setPurchaseHistSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setPurchaseHistSection({ data: null, loading: true, error: null });
        const purchaseHist = await apiService.getPartPurchaseHist(partNum);
        setPurchaseHistSection({
          data: purchaseHist,
          loading: false,
          error: null,
        });
        setCachedData(cacheKey, purchaseHist);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load purchase history';
        setPurchaseHistSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchPurchaseHist();
  }, [partNum]);

  // Fetch transaction history (independent)
  useEffect(() => {
    const fetchTransHist = async () => {
      if (!partNum) return;

      try {
        setTransHistSection((prev) => ({
          ...prev,
          loading: true,
          error: null,
        }));
        const response = await apiService.getPartTransHist(partNum, 1, 20);
        setTransHistSection({
          data: response.transHist || [],
          loading: false,
          loadingMore: false,
          error: null,
          morePages: response.morePages || false,
          currentPage: 1,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load transaction history';
        setTransHistSection((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    };

    fetchTransHist();
  }, [partNum]);

  // Fetch a specific page of transaction history
  const fetchTransHistPage = useCallback(
    async (page: number) => {
      if (!partNum || transHistSection.loading) return;

      setTransHistSection((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await apiService.getPartTransHist(partNum, page, 20);
        setTransHistSection({
          data: response.transHist || [],
          loading: false,
          loadingMore: false,
          error: null,
          morePages: response.morePages || false,
          currentPage: page,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load transaction history';
        setTransHistSection((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    },
    [partNum, transHistSection.loading]
  );

  const handleTransHistPreviousPage = useCallback(() => {
    if (transHistSection.currentPage > 1) {
      fetchTransHistPage(transHistSection.currentPage - 1);
    }
  }, [transHistSection.currentPage, fetchTransHistPage]);

  const handleTransHistNextPage = useCallback(() => {
    if (transHistSection.morePages) {
      fetchTransHistPage(transHistSection.currentPage + 1);
    }
  }, [
    transHistSection.morePages,
    transHistSection.currentPage,
    fetchTransHistPage,
  ]);

  // Fetch quote history (independent)
  useEffect(() => {
    const fetchQuoteHist = async () => {
      if (!partNum) return;

      const cacheKey = `partQuoteHist_${partNum}`;
      const cached = getCachedData<QuoteHistory[]>(cacheKey);
      if (cached) {
        setQuoteHistSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setQuoteHistSection({ data: null, loading: true, error: null });
        const quoteHist = await apiService.getPartQuoteHistory(partNum);
        setQuoteHistSection({ data: quoteHist, loading: false, error: null });
        setCachedData(cacheKey, quoteHist);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load quote history';
        setQuoteHistSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchQuoteHist();
  }, [partNum]);

  // Fetch approved suppliers (independent)
  useEffect(() => {
    const fetchApprovedSuppliers = async () => {
      if (!partNum) return;

      const cacheKey = `partApprovedSuppliers_${partNum}`;
      const cached = getCachedData<ApprovedSupplier[]>(cacheKey);
      if (cached) {
        setApprovedSuppliersSection({
          data: cached,
          loading: false,
          error: null,
        });
        return;
      }

      try {
        setApprovedSuppliersSection({ data: null, loading: true, error: null });
        const approvedSuppliers =
          await apiService.getPartApprovedSuppliers(partNum);
        setApprovedSuppliersSection({
          data: approvedSuppliers,
          loading: false,
          error: null,
        });
        setCachedData(cacheKey, approvedSuppliers);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load approved suppliers';
        setApprovedSuppliersSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchApprovedSuppliers();
  }, [partNum]);

  // Fetch customer references (independent)
  useEffect(() => {
    const fetchCustomerRefs = async () => {
      if (!partNum) return;

      const cacheKey = `partCustomerRefs_${partNum}`;
      const cached = getCachedData<CustomerRef[]>(cacheKey);
      if (cached) {
        setCustomerRefsSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setCustomerRefsSection({ data: null, loading: true, error: null });
        const customerRefs = await apiService.getPartCustomerRefs(partNum);
        setCustomerRefsSection({
          data: customerRefs,
          loading: false,
          error: null,
        });
        setCachedData(cacheKey, customerRefs);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load customer references';
        setCustomerRefsSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchCustomerRefs();
  }, [partNum]);

  // Fetch order history (independent)
  useEffect(() => {
    const fetchOrderHist = async () => {
      if (!partNum) return;

      const cacheKey = `partOrderHist_${partNum}`;
      const cached = getCachedData<OrderHistory[]>(cacheKey);
      if (cached) {
        setOrderHistSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setOrderHistSection({ data: null, loading: true, error: null });
        const orderHist = await apiService.getPartOrderHistory(partNum);
        setOrderHistSection({ data: orderHist, loading: false, error: null });
        setCachedData(cacheKey, orderHist);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load order history';
        setOrderHistSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchOrderHist();
  }, [partNum]);

  // Fetch production history (independent)
  useEffect(() => {
    const fetchProdHist = async () => {
      if (!partNum) return;

      const cacheKey = `partProdHist_${partNum}`;
      const cached = getCachedData<ProdHistory[]>(cacheKey);
      if (cached) {
        setProdHistSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setProdHistSection({ data: null, loading: true, error: null });
        const prodHist = await apiService.getPartProdHistory(partNum);
        setProdHistSection({ data: prodHist, loading: false, error: null });
        setCachedData(cacheKey, prodHist);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load production history';
        setProdHistSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchProdHist();
  }, [partNum]);

  // Fetch customer price list (independent)
  useEffect(() => {
    const fetchCustomerPrice = async () => {
      if (!partNum) return;

      const cacheKey = `partCustomerPrice_${partNum}`;
      const cached = getCachedData<CustomerPriceList>(cacheKey);
      if (cached) {
        setCustomerPriceSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setCustomerPriceSection({ data: null, loading: true, error: null });
        const customerPriceList =
          await apiService.getCustomerPriceList(partNum);
        setCustomerPriceSection({
          data: customerPriceList,
          loading: false,
          error: null,
        });
        setCachedData(cacheKey, customerPriceList);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load customer price list';
        setCustomerPriceSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchCustomerPrice();
  }, [partNum]);

  // Fetch method/BOM tree (independent, lazy loading on expand)
  useEffect(() => {
    const fetchMethod = async () => {
      if (!partNum) return;

      const cacheKey = `partMethod_${partNum}`;
      const treeStateCacheKey = `partMethodTreeState_${partNum}`;
      const cached = getCachedData<{
        data: MethodNode | null;
        noMethod: boolean;
      }>(cacheKey);
      if (cached) {
        setMethodSection({ data: cached.data, loading: false, error: null });
        // Also restore tree state if available
        const cachedTreeState =
          getCachedData<MethodTreeState>(treeStateCacheKey);
        if (cachedTreeState) {
          setMethodTreeState(cachedTreeState);
        }
        return;
      }

      try {
        setMethodSection({ data: null, loading: true, error: null });
        const methodNode = await apiService.getMethod(partNum);
        setMethodSection({ data: methodNode, loading: false, error: null });
        setCachedData(cacheKey, { data: methodNode, noMethod: false });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load method/BOM';
        // Check if it's a 400 error (no method available for this part)
        if (errorMessage.includes('400')) {
          setMethodSection({
            data: null,
            loading: false,
            error: null,
          });
          setCachedData(cacheKey, { data: null, noMethod: true });
        } else {
          setMethodSection({
            data: null,
            loading: false,
            error: errorMessage,
          });
        }
      }
    };

    fetchMethod();
  }, [partNum]);

  // Fetch where used data (independent, shows parent assemblies)
  useEffect(() => {
    const fetchWhereUsed = async () => {
      if (!partNum) return;

      const cacheKey = `partWhereUsed_${partNum}`;
      const cached = getCachedData<WhereUsed[]>(cacheKey);
      if (cached) {
        setWhereUsedSection({ data: cached, loading: false, error: null });
        return;
      }

      try {
        setWhereUsedSection({ data: null, loading: true, error: null });
        const whereUsed = await apiService.getPartWhereUsed(partNum);
        setWhereUsedSection({ data: whereUsed, loading: false, error: null });
        setCachedData(cacheKey, whereUsed);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load where used data';
        setWhereUsedSection({
          data: null,
          loading: false,
          error: errorMessage,
        });
      }
    };

    fetchWhereUsed();
  }, [partNum]);

  // Handle lazy loading of material children
  const handleMaterialExpand = async (
    childPartNum: string
  ): Promise<MethodNode | null> => {
    try {
      const methodNode = await apiService.getMethod(childPartNum);
      return methodNode;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load material';
      // Check if it's a 400 error (no method available - leaf node)
      if (errorMessage.includes('400')) {
        return null;
      }
      console.error('Failed to load material children:', error);
      return null;
    }
  };

  // Handle method tree state changes (for caching expanded state)
  const handleMethodTreeStateChange = useCallback(
    (state: MethodTreeState) => {
      if (!partNum) return;
      const treeStateCacheKey = `partMethodTreeState_${partNum}`;
      setCachedData(treeStateCacheKey, state);
    },
    [partNum]
  );

  // Handle quick search - validate part exists before navigating
  // Supports both UM part numbers (e.g., UMK-94129914) and ERP part numbers
  const handleQuickSearch = async () => {
    const trimmed = quickSearchInput.trim();
    if (!trimmed) return;

    try {
      setQuickSearchLoading(true);
      setQuickSearchError(null);

      // First, try to look up as a UM part number (e.g., UMK-94129914)
      const erpPartNum = await apiService.lookupUMPart(trimmed);

      if (erpPartNum) {
        // Found via UM lookup, navigate to the ERP part number
        navigate(`/parts/${encodeURIComponent(erpPartNum)}`);
        setQuickSearchInput('');
        return;
      }

      // Not found via UM lookup, try direct part details lookup
      await apiService.getPartDetails(trimmed);
      // Part exists, navigate to it
      navigate(`/parts/${encodeURIComponent(trimmed)}`);
      setQuickSearchInput('');
    } catch {
      // Part not found, show error without changing URL
      setQuickSearchError(trimmed);
    } finally {
      setQuickSearchLoading(false);
    }
  };

  const handleQuickSearchKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      handleQuickSearch();
    }
  };

  if (mainLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 sm:p-6 w-full">
        {/* Quick Search and Breadcrumb Row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <Breadcrumb
            backLabel={t('navigation.partsDashboard')}
            currentPage={partNum || ''}
          />

          {/* Quick Search */}
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t(
                  'parts.search.quickSearchPlaceholder',
                  'Jump to part...'
                )}
                value={quickSearchInput}
                onChange={(e) => {
                  setQuickSearchInput(e.target.value);
                  setQuickSearchError(null);
                }}
                onKeyPress={handleQuickSearchKeyPress}
                disabled={quickSearchLoading}
                className="pl-9 pr-3 py-2 w-48 sm:w-64 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
            <button
              onClick={handleQuickSearch}
              disabled={!quickSearchInput.trim() || quickSearchLoading}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {quickSearchLoading ? '...' : t('parts.search.go', 'Go')}
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back', 'Back')}
          </Button>
          {partDetails && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Link
                to={`/picture-processor?partNum=${encodeURIComponent(partNum || '')}&autoConfirm=1`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 px-4 bg-blue-600 text-white hover:bg-blue-700"
                title={t('parts.pictures.addPhoto')}
              >
                <Camera className="w-4 h-4" />
                {t('parts.pictures.addPhoto')}
              </Link>
              <Link
                to={`/label-printing?type=internal-part&partNumber=${encodeURIComponent(partNum || '')}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 px-4 bg-green-600 text-white hover:bg-green-700"
                title="Print label for this part"
              >
                <Printer className="w-4 h-4" />
                Print Label
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="w-full p-4 sm:p-6">
        {/* Part Not Found Banner */}
        {mainError && (
          <div className="bg-red-100 border border-red-300 px-4 py-3 mb-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">
              {t(
                'parts.search.partNotFoundBanner',
                'Part "{{partNum}}" not found',
                { partNum }
              )}
            </span>
          </div>
        )}

        {/* Quick Search Error Banner */}
        {quickSearchError && (
          <div className="bg-red-100 border border-red-300 px-4 py-3 mb-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">
              {t(
                'parts.search.partNotFoundBanner',
                'Part "{{partNum}}" not found',
                { partNum: quickSearchError }
              )}
            </span>
          </div>
        )}

        {/* Inactive Part Warning Banner */}
        {partDetails?.isInactive && (
          <div className="bg-red-100 border border-red-300 px-4 py-3 mb-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-semibold">
              {t('parts.inactiveBanner', 'This part is INACTIVE')}
            </span>
          </div>
        )}

        {/* Main Part Information Section */}
        {partDetails && (
          <div className="space-y-4">
            {/* TOP: Part Information - Full Width Horizontal Layout */}
            <PartInformationSection
              partDetails={partDetails}
              pictures={picturesSection.data}
              picturesLoading={picturesSection.loading}
            />

            {/* TWO COLUMN LAYOUT: Left and Right columns. min-w-0 lets each column share the row
                instead of growing to its widest table; the tables scroll inside their cards. */}
            <div className="flex flex-col lg:flex-row gap-4">
              {/* LEFT COLUMN: Warehouse Bins, Forecast, Production History, Purchase History, Approved Suppliers, Where Used */}
              <div className="flex-1 min-w-0 flex flex-col gap-4">
                {/* Bins Section */}
                <div>
                  {binsSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {binsSection.error && (
                    <ErrorBanner
                      error={binsSection.error}
                      onDismiss={() =>
                        setBinsSection({ ...binsSection, error: null })
                      }
                    />
                  )}
                  {!binsSection.loading && binsSection.data !== null && (
                    <PartBinsTable partBins={binsSection.data} />
                  )}
                </div>

                {partNum && <PartForecastCard partNum={partNum} />}

                {/* Production History Section */}
                <div>
                  {prodHistSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {prodHistSection.error && (
                    <ErrorBanner
                      error={prodHistSection.error}
                      onDismiss={() =>
                        setProdHistSection({
                          ...prodHistSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!prodHistSection.loading &&
                    prodHistSection.data !== null && (
                      <PartProdHistTable prodHistory={prodHistSection.data} />
                    )}
                </div>

                {/* Purchase History Section */}
                <div>
                  {purchaseHistSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {purchaseHistSection.error && (
                    <ErrorBanner
                      error={purchaseHistSection.error}
                      onDismiss={() =>
                        setPurchaseHistSection({
                          ...purchaseHistSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!purchaseHistSection.loading &&
                    purchaseHistSection.data !== null && (
                      <PartPurchaseHistTable
                        purchaseHist={purchaseHistSection.data}
                      />
                    )}
                </div>

                {/* Approved Suppliers Section */}
                <div>
                  {approvedSuppliersSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {approvedSuppliersSection.error && (
                    <ErrorBanner
                      error={approvedSuppliersSection.error}
                      onDismiss={() =>
                        setApprovedSuppliersSection({
                          ...approvedSuppliersSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!approvedSuppliersSection.loading &&
                    approvedSuppliersSection.data !== null && (
                      <PartApprovedSuppliersTable
                        approvedSuppliers={approvedSuppliersSection.data}
                      />
                    )}
                </div>

                {/* Where Used Section */}
                <div>
                  {whereUsedSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {whereUsedSection.error && (
                    <ErrorBanner
                      error={whereUsedSection.error}
                      onDismiss={() =>
                        setWhereUsedSection({
                          ...whereUsedSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!whereUsedSection.loading &&
                    !whereUsedSection.error &&
                    whereUsedSection.data !== null && (
                      <PartWhereUsedTable whereUsed={whereUsedSection.data} />
                    )}
                </div>
              </div>

              {/* RIGHT COLUMN: Time Phased, Transaction History, Order History, Quote History, Customer Price List, Customer References */}
              <div className="flex-1 min-w-0 flex flex-col gap-4">
                {/* Time Phased Section */}
                <div>
                  {timePhasedSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {timePhasedSection.error && (
                    <ErrorBanner
                      error={timePhasedSection.error}
                      onDismiss={() =>
                        setTimePhasedSection({
                          ...timePhasedSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!timePhasedSection.loading &&
                    timePhasedSection.data !== null && (
                      <PartTimePhasedTable
                        timePhased={timePhasedSection.data}
                      />
                    )}
                </div>

                {/* Transaction History Section */}
                <div>
                  {transHistSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {transHistSection.error && (
                    <ErrorBanner
                      error={transHistSection.error}
                      onDismiss={() =>
                        setTransHistSection({
                          ...transHistSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!transHistSection.loading && !transHistSection.error && (
                    <PartTransHistTable
                      transHist={transHistSection.data}
                      currentPage={transHistSection.currentPage}
                      pageSize={20}
                      morePages={transHistSection.morePages}
                      isLoading={transHistSection.loading}
                      onPreviousPage={handleTransHistPreviousPage}
                      onNextPage={handleTransHistNextPage}
                    />
                  )}
                </div>

                {/* Order History Section */}
                <div>
                  {orderHistSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {orderHistSection.error && (
                    <ErrorBanner
                      error={orderHistSection.error}
                      onDismiss={() =>
                        setOrderHistSection({
                          ...orderHistSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!orderHistSection.loading &&
                    orderHistSection.data !== null && (
                      <PartOrderHistoryTable
                        orderHistory={orderHistSection.data}
                      />
                    )}
                </div>

                {/* Quote History Section */}
                <div>
                  {quoteHistSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {quoteHistSection.error && (
                    <ErrorBanner
                      error={quoteHistSection.error}
                      onDismiss={() =>
                        setQuoteHistSection({
                          ...quoteHistSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!quoteHistSection.loading &&
                    quoteHistSection.data !== null && (
                      <PartQuoteHistoryTable
                        quoteHistory={quoteHistSection.data}
                      />
                    )}
                </div>

                {/* Customer Price List Section */}
                <div>
                  {customerPriceSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {customerPriceSection.error && (
                    <ErrorBanner
                      error={customerPriceSection.error}
                      onDismiss={() =>
                        setCustomerPriceSection({
                          ...customerPriceSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!customerPriceSection.loading &&
                    customerPriceSection.data !== null && (
                      <PartCustomerPriceTable
                        customerPriceList={customerPriceSection.data}
                      />
                    )}
                </div>

                {/* Customer References Section */}
                <div>
                  {customerRefsSection.loading && (
                    <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {customerRefsSection.error && (
                    <ErrorBanner
                      error={customerRefsSection.error}
                      onDismiss={() =>
                        setCustomerRefsSection({
                          ...customerRefsSection,
                          error: null,
                        })
                      }
                    />
                  )}
                  {!customerRefsSection.loading &&
                    customerRefsSection.data !== null && (
                      <PartCustomerRefsTable
                        customerRefs={customerRefsSection.data}
                      />
                    )}
                </div>
              </div>
            </div>

            {/* Method/BOM Tree Section - Full Width */}
            <div>
              {methodSection.loading && (
                <div className="bg-white rounded border border-gray-300 p-4 flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              )}
              {methodSection.error && (
                <ErrorBanner
                  error={methodSection.error}
                  onDismiss={() =>
                    setMethodSection({
                      ...methodSection,
                      error: null,
                    })
                  }
                />
              )}
              {!methodSection.loading && methodSection.data !== null && (
                <MethodTree
                  methodData={methodSection.data}
                  onMaterialExpand={handleMaterialExpand}
                  initialState={methodTreeState}
                  onStateChange={handleMethodTreeStateChange}
                />
              )}
              {!methodSection.loading &&
                !methodSection.error &&
                methodSection.data === null && (
                  <div className="bg-white rounded border border-gray-300 p-6 text-center">
                    <p className="text-gray-500">
                      {t('parts.method.noMethod')}
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!partDetails && !mainLoading && !mainError && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500">No part details available</p>
          </div>
        )}
      </div>
    </div>
  );
};
